import {
  BUSINESS_KINDS,
  BUSINESS_KIND_LABELS,
  BUSINESS_KIND_LIST_LABELS,
  type BusinessKind,
  LEAD_SEGMENTS,
  LEAD_SEGMENT_LIST_LABELS,
  type LeadSegment,
  EQUIPMENT_TYPE_LABELS,
  WEBSITE_LOOKUP_STATUS_LABELS,
  type ContactDTO,
  type EquipmentType,
  type LeadDetailDTO,
  type LeadWebsiteEmailResponse,
  type TaskDTO,
  type UpdateLeadBody,
} from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowRightLeft,
  Ban,
  CalendarPlus,
  ChevronDown,
  CheckCircle2,
  ExternalLink,
  Globe,
  Mail,
  MapPin,
  MessageSquarePlus,
  MoreHorizontal,
  Phone,
  Plus,
  StickyNote,
  Trash2,
  Undo2,
  UserPlus,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { Page } from '../../components/layout/Page';
import { PageHeader } from '../../components/layout/PageHeader';
import { MoneyText, NextActionChip, OwnerAvatar, RelationshipBadge, RelativeTime, ScorePill, StageBadge, TierBadge } from '../../components/crm';
import { Badge, Button, Card, CardBody, CardHeader, DropdownMenu, EmptyState, IconButton, Skeleton, Tabs, useConfirm, useToast } from '../../components/ui';
import { useAuth } from '../../hooks/useAuth';
import { useHotkeys } from '../../hooks/useHotkeys';
import { useLookups } from '../../hooks/useMeta';
import { useNavSection } from '../../hooks/useNavSection';
import { api, ApiError, errorMessage } from '../../lib/api';
import { formatDate, formatPhone, pluralize } from '../../lib/format';
import { EmailComposer } from '../email/EmailComposer';
import { TaskFormPanel } from '../tasks/TaskFormPanel';
import { InterestsAndTags, ProposalsCard, ScoreCard } from './profile/cards';
import {
  ContactPanel,
  EquipmentPanel,
  LogCallPanel,
  LogReplyPanel,
  NewOpportunityPanel,
  NotePanel,
} from './profile/panels';
import { DealsTab, EmailsTab, TasksTab, TimelineTab } from './profile/tabs';
import { LeadAiPanel } from '../ai/LeadAiPanel';
import { LeadAutomationCard } from '../automation/LeadAutomationCard';
import { pagePath } from '../discovery/model';
import { kindNoun, listOf } from './model';

type TabKey = 'timeline' | 'deals' | 'tasks' | 'emails';

/** Dealers and reps are told apart from pizzerias at a glance; pizzerias show their score and tier instead. */
const KIND_BADGE_COLORS: Record<Exclude<BusinessKind, 'pizzeria'>, string> = { dealer: 'sky', rep: 'violet' };

const TAB_KEYS: TabKey[] = ['timeline', 'deals', 'tasks', 'emails'];

/**
 * The lead profile. Ordering matters: identity and the next best action first, then the account details, then
 * the history. Every mutation invalidates the lead, the list and the dashboard so numbers never go stale.
 */
export function LeadProfilePage() {
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const { can } = useAuth();
  const { meta } = useLookups();

  const requestedTab = params.get('tab') as TabKey | null;
  const tab: TabKey = requestedTab && TAB_KEYS.includes(requestedTab) ? requestedTab : 'timeline';
  const [composer, setComposer] = useState(false);
  const [logCall, setLogCall] = useState(false);
  const [logReply, setLogReply] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<TaskDTO | null>(null);
  const [opportunityOpen, setOpportunityOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState<ContactDTO | null | 'new'>(null);
  const [equipmentOpen, setEquipmentOpen] = useState(false);

  const { data: lead, isLoading } = useQuery({
    queryKey: ['leads', id],
    queryFn: ({ signal }) => api.get<LeadDetailDTO>(`/api/leads/${id}`, { signal }),
    enabled: id !== '',
  });
  // Every business lives at /leads/:id; the sidebar shows the list it belongs to (Leads, Prospects, Clients, Test Kitchens, Dealers…).
  useNavSection(lead ? listOf(lead).path : null);
  // "lead" for pizzerias, "dealer" / "rep" otherwise — used in this page's actions and prompts.
  const noun = kindNoun(lead?.kind ?? 'pizzeria');

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['leads', id] }),
      queryClient.invalidateQueries({ queryKey: ['leads', 'list'] }),
      queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
    ]);
  };

  useHotkeys({
    e: () => setComposer(true),
    c: () => setLogCall(true),
    o: () => setNoteOpen(true),
    t: () => setTaskOpen(true),
  });

  const verify = useMutation({
    mutationFn: () => api.post(`/api/leads/${id}/verify`),
    onSuccess: async () => {
      toast.success('Details verified');
      await invalidate();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const findWebsiteEmail = useMutation({
    mutationFn: () => api.post<LeadWebsiteEmailResponse>(`/api/leads/${id}/website-email`, {}),
    // Reading a site (home page, robots.txt, up to two contact pages) can take up to ~30 s: say it started.
    onMutate: () => toast.info('Reading the website for an email address — this can take up to 30 seconds'),
    onSuccess: async ({ lookup, outcome }) => {
      const email = lookup.email ?? '';
      const page = lookup.sourceUrl ? pagePath(lookup.sourceUrl) : 'the website';
      if (outcome === 'contact_added') toast.success(`Added ${email} from ${page}`);
      else if (outcome === 'consent_recorded') toast.success(`Recorded consent for ${email} (published on the website)`);
      else if (outcome === 'already_known') toast.info(`${email} is already on this lead`);
      else if (outcome === 'proposed') toast.info(`Found ${email} — it differs from the lead's email; review it in the lead's pending changes`);
      else toast.info(lookup.message ?? (lookup.status === 'pending' ? 'The website is queued to be read' : WEBSITE_LOOKUP_STATUS_LABELS[lookup.status]));
      // ['leads', id] also covers the timeline (['leads', id, 'timeline', …]), where the change is recorded.
      await invalidate();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const deleteLead = async () => {
    if (!(await confirm({ title: `Delete ${lead?.name}?`, message: 'It disappears from the pipeline but a manager can restore it.', confirmLabel: 'Delete', danger: true }))) return;
    try {
      await api.delete(`/api/leads/${id}`);
      toast.success(`${noun.charAt(0).toUpperCase()}${noun.slice(1)} deleted`);
      await queryClient.invalidateQueries({ queryKey: ['leads'] });
      navigate(lead ? listOf(lead).path : '/leads');
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const setDoNotContact = async () => {
    const reason = window.prompt(`Why should this ${noun} never be contacted again?`);
    if (!reason || reason.trim().length < 3) return;
    try {
      await api.post(`/api/leads/${id}/do-not-contact`, { reason: reason.trim() });
      toast.success('Marked do not contact — all outreach suppressed');
      await invalidate();
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const clearDoNotContact = async () => {
    const reason = window.prompt(`Why may we contact this ${noun} again?`);
    if (!reason || reason.trim().length < 3) return;
    try {
      await api.delete(`/api/leads/${id}/do-not-contact`, { reason: reason.trim() });
      toast.success('Do-not-contact cleared');
      await invalidate();
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  /** PATCH against the loaded version; on any outcome reload so the page shows what is saved now. */
  const patchLead = async (patch: Omit<UpdateLeadBody, 'version'>, done: string) => {
    if (!lead) return;
    try {
      await api.patch(`/api/leads/${id}`, { ...patch, version: lead.version });
      toast.success(done);
    } catch (error) {
      // Someone else saved the record first: reload it so a retry starts from what is there now.
      toast.error(
        error instanceof ApiError && error.code === 'stale_version'
          ? 'This record changed since you opened it — it was reloaded. Check it and try again.'
          : errorMessage(error),
      );
    }
    await invalidate();
  };

  const moveTo = async (patch: { kind: BusinessKind } | { segment: LeadSegment }, list: string) => {
    if (!lead) return;
    const ok = await confirm({
      title: `Move ${lead.name} to ${list}?`,
      message: `It leaves ${listOf(lead).label} and appears under ${list}. Its contacts, history, deals and tasks stay with it.`,
      confirmLabel: 'Move',
    });
    if (ok) await patchLead(patch, `Moved to ${list}`);
  };

  if (isLoading || !lead) {
    return (
      <Page>
        <Skeleton className="mb-4 h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </Page>
    );
  }

  const _openOpportunities = lead.counts.openOpportunities;
  const nextAction = lead.nextAction;
  // Fit score, tier, pizza details, installed equipment and purchase timing only apply to pizzerias.
  const isPizzeria = lead.kind === 'pizzeria';
  const canEdit = can('lead:write');
  const tiers = [...(meta?.tiers ?? [])].sort((a, b) => b.minScore - a.minScore);
  const scoreTierLabel = tiers.find((t) => t.key === lead.score.tierKey)?.label ?? lead.score.tierKey;
  return (
    <Page width="wide">
      <PageHeader
        title={lead.name}
        description={
          <span className="flex flex-wrap items-center gap-2">
            {lead.city && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="size-3.5" /> {[lead.city, lead.region].filter(Boolean).join(', ')}
              </span>
            )}
            {isPizzeria && lead.restaurantType && <span>{lead.restaurantType}</span>}
            {lead.phone && (
              <a className="inline-flex items-center gap-1 hover:text-ember-700" href={`tel:${lead.phone}`}>
                <Phone className="size-3.5" /> {formatPhone(lead.phone)}
              </a>
            )}
            {lead.website && (
              <a className="inline-flex items-center gap-1 hover:text-ember-700" href={lead.website} target="_blank" rel="noreferrer">
                <ExternalLink className="size-3.5" /> {lead.websiteDomain ?? 'website'}
              </a>
            )}
          </span>
        }
        breadcrumbs={[{ label: listOf(lead).label, to: listOf(lead).path }, { label: lead.name }]}
        meta={
          <span className="flex flex-wrap items-center gap-2">
            {lead.kind === 'pizzeria' ? (
              <>
                <ScorePill score={lead.score.total} tierKey={lead.tierKey} />
                {canEdit ? (
                  <DropdownMenu
                    aria-label="Change tier"
                    trigger={(triggerProps) => (
                      <button type="button" {...triggerProps} className="inline-flex items-center gap-0.5 rounded-full" title="Change tier">
                        <TierBadge tierKey={lead.tierKey} />
                        <ChevronDown className="size-3.5 text-slate-400" />
                      </button>
                    )}
                    items={[
                      { type: 'label', key: 'tier-label', label: 'Set tier by hand' },
                      ...tiers.map((tier) => ({
                        key: `tier:${tier.key}`,
                        label: lead.tierOverrideKey === tier.key ? `${tier.label} ✓` : tier.label,
                        onSelect: () => void patchLead({ tierOverrideKey: tier.key }, `Tier set to ${tier.label}`),
                      })),
                      { type: 'separator', key: 'tier-sep' },
                      {
                        key: 'tier:auto',
                        label: `Automatic from score (${scoreTierLabel})${lead.tierOverrideKey === null ? ' ✓' : ''}`,
                        disabled: lead.tierOverrideKey === null,
                        onSelect: () => void patchLead({ tierOverrideKey: null }, `Tier follows the score again (${scoreTierLabel})`),
                      },
                    ]}
                  />
                ) : (
                  <TierBadge tierKey={lead.tierKey} />
                )}
                {lead.tierOverrideKey && <span className="text-xs text-slate-500">set by hand</span>}
              </>
            ) : (
              <Badge color={KIND_BADGE_COLORS[lead.kind]}>{BUSINESS_KIND_LABELS[lead.kind]}</Badge>
            )}
            {canEdit ? (
              <DropdownMenu
                aria-label="Change relationship"
                trigger={(triggerProps) => (
                  <button type="button" {...triggerProps} className="inline-flex items-center gap-0.5 rounded-full" title="Change relationship">
                    <RelationshipBadge relationshipId={lead.relationshipId} />
                    <ChevronDown className="size-3.5 text-slate-400" />
                  </button>
                )}
                items={[
                  { type: 'label', key: 'rel-label', label: 'Relationship' },
                  ...(meta?.relationships ?? [])
                    .filter((r) => r.isActive)
                    .map((r) => ({
                      key: `rel:${r.key}`,
                      label: r.id === lead.relationshipId ? `${r.label} ✓` : r.label,
                      disabled: r.id === lead.relationshipId,
                      onSelect: () => void patchLead({ relationshipKey: r.key }, `Relationship set to ${r.label}`),
                    })),
                ]}
              />
            ) : (
              <RelationshipBadge relationshipId={lead.relationshipId} />
            )}
            {isPizzeria && lead.purchaseTimeframe !== 'unknown' && <Badge color="slate">{lead.purchaseTimeframe.replace(/_/g, ' ')}</Badge>}
          </span>
        }
        actions={
          <>
            <Button variant="primary" icon={Mail} onClick={() => setComposer(true)} disabled={lead.doNotContact !== null}>
              Email
            </Button>
            <Button icon={Phone} onClick={() => setLogCall(true)}>
              Log call
            </Button>
            <Button icon={MessageSquarePlus} onClick={() => setLogReply(true)}>
              Log reply
            </Button>
            <Button icon={StickyNote} onClick={() => setNoteOpen(true)}>
              Note
            </Button>
            <Button icon={CalendarPlus} onClick={() => setTaskOpen(true)}>
              Task
            </Button>
            <Button icon={Plus} onClick={() => setOpportunityOpen(true)}>
              Deal
            </Button>
            <DropdownMenu
              trigger={(triggerProps) => <IconButton {...triggerProps} icon={MoreHorizontal} label="More actions" />}
              items={[
                { key: 'verify', label: 'Mark details as verified', icon: CheckCircle2, onSelect: () => verify.mutate() },
                ...(lead.website && can('lead:write')
                  ? [
                      {
                        key: 'website-email',
                        label: findWebsiteEmail.isPending ? 'Reading the website…' : 'Find email on website',
                        icon: Globe,
                        disabled: findWebsiteEmail.isPending,
                        onSelect: () => findWebsiteEmail.mutate(),
                      },
                    ]
                  : []),
                // Signals feed the pizzeria fit score, which dealers and reps do not have.
                ...(isPizzeria
                  ? [
                      { key: 'signal', type: 'label' as const, label: 'Record a signal' },
                      ...(meta?.signalRules ?? []).map((rule) => ({
                        key: `signal:${rule.key}`,
                        label: rule.label,
                        onSelect: () => void recordSignal(rule.key),
                      })),
                    ]
                  : []),
                ...(can('lead:write')
                  ? [
                      { type: 'separator' as const, key: 'sep-kind' },
                      // Pizzerias move between Leads, Prospects, Clients and Test Kitchens; any business can change kind.
                      ...(isPizzeria ? LEAD_SEGMENTS.filter((target) => target !== lead.segment) : []).map((target) => ({
                        key: `segment:${target}`,
                        label: `Move to ${LEAD_SEGMENT_LIST_LABELS[target]}`,
                        icon: ArrowRightLeft,
                        onSelect: () => void moveTo({ segment: target }, LEAD_SEGMENT_LIST_LABELS[target]),
                      })),
                      ...BUSINESS_KINDS.filter((target) => target !== lead.kind).map((target) => ({
                        key: `kind:${target}`,
                        label: `Move to ${BUSINESS_KIND_LIST_LABELS[target]}`,
                        icon: ArrowRightLeft,
                        onSelect: () => void moveTo({ kind: target }, BUSINESS_KIND_LIST_LABELS[target]),
                      })),
                    ]
                  : []),
                { type: 'separator', key: 'sep1' },
                ...(lead.doNotContact
                  ? [{ key: 'dnc', label: 'Clear do-not-contact', icon: Undo2, onSelect: () => void clearDoNotContact() }]
                  : [{ key: 'dnc', label: 'Mark do not contact', icon: Ban, danger: true, onSelect: () => void setDoNotContact() }]),
                ...(can('lead:delete') ? [{ key: 'delete', label: `Delete ${noun}`, icon: Trash2, danger: true, onSelect: () => void deleteLead() }] : []),
              ]}
            />
          </>
        }
      />

      {lead.doNotContact && (
        <Card className="mb-4 border-red-300 bg-red-50">
          <CardBody className="flex items-start gap-2 text-[13px] text-red-900">
            <Ban className="mt-0.5 size-4" />
            <div>
              <p className="font-medium">Do not contact</p>
              <p>
                {lead.doNotContact.reason ?? 'No reason recorded'} — {lead.doNotContact.at ? formatDate(lead.doNotContact.at) : ''}. All addresses are suppressed:
                manual, bulk and automated email are blocked.
              </p>
            </div>
          </CardBody>
        </Card>
      )}

      {lead.deleted && (
        <Card className="mb-4 border-slate-300 bg-slate-50">
          <CardBody className="text-[13px] text-slate-700">
            This lead was deleted {formatDate(lead.deleted.at)}. A manager can restore it from the lead table (“Deleted leads” filter).
          </CardBody>
        </Card>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 space-y-4">
          <Card className={nextAction && nextAction !== 'no_action' && nextAction !== 'do_not_contact' ? 'border-ember-300 bg-ember-50' : undefined}>
            <CardBody className="flex flex-wrap items-center gap-3">
              <NextActionChip action={nextAction} reason={lead.nextActionReason} />
              <p className="min-w-0 flex-1 text-[13px] text-slate-700">{lead.nextActionReason ?? `Nothing needs your attention on this ${noun} right now.`}</p>
              {nextAction === 'contact_now' && !lead.doNotContact && (
                <Button size="sm" variant="primary" icon={Mail} onClick={() => setComposer(true)}>
                  Send the email
                </Button>
              )}
              {nextAction === 'respond_to_reply' && (
                <Button size="sm" variant="primary" icon={Mail} onClick={() => setComposer(true)}>
                  Reply
                </Button>
              )}
              {nextAction === 'call' && (
                <Button size="sm" variant="primary" icon={Phone} onClick={() => setLogCall(true)}>
                  Log the call
                </Button>
              )}
              {nextAction === 'research_contact' && (
                <Button size="sm" variant="primary" icon={UserPlus} onClick={() => setContactOpen('new')}>
                  Add a contact
                </Button>
              )}
              {nextAction === 'review_duplicate' && lead.duplicates[0] && (
                <Button size="sm" variant="primary" onClick={() => navigate(`/duplicates/${lead.duplicates[0]!.candidateId}`)}>
                  Review duplicate
                </Button>
              )}
            </CardBody>
          </Card>

          <Card>
            <Tabs
              tabs={[
                { key: 'timeline', label: 'Timeline' },
                { key: 'deals', label: 'Deals', count: lead.counts.openOpportunities },
                { key: 'tasks', label: 'Tasks', count: lead.counts.openTasks },
                { key: 'emails', label: 'Emails', count: lead.counts.emails },
              ]}
              value={tab}
              onChange={(key) => {
                const next = new URLSearchParams(params);
                next.set('tab', key);
                setParams(next, { replace: true });
              }}
            />
            <CardBody>
              {tab === 'timeline' && <TimelineTab businessId={id} />}
              {tab === 'deals' && <DealsTab businessId={id} onNew={() => setOpportunityOpen(true)} />}
              {tab === 'tasks' && <TasksTab businessId={id} onNew={() => setTaskOpen(true)} onEdit={setEditingTask} />}
              {tab === 'emails' && <EmailsTab businessId={id} onCompose={() => setComposer(true)} />}
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Contacts"
              description="Who you talk to and whether you may email them."
              actions={
                <Button size="sm" icon={UserPlus} onClick={() => setContactOpen('new')}>
                  Add
                </Button>
              }
            />
            <CardBody className="space-y-2">
              {lead.contacts.length === 0 ? (
                <EmptyState
                  compact
                  icon={UserPlus}
                  title="No contact yet"
                  description="Add the owner or manager so outreach has a person behind it."
                  action={<Button icon={UserPlus} onClick={() => setContactOpen('new')}>Add a contact</Button>}
                />
              ) : (
                lead.contacts.map((contact) => (
                  <button
                    key={contact.id}
                    type="button"
                    onClick={() => setContactOpen(contact)}
                    className="flex w-full flex-wrap items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-left text-[13px] hover:border-ember-300 hover:bg-ember-50"
                  >
                    <span className="font-medium text-slate-900">{contactDisplayName(contact)}</span>
                    {contact.isPrimary && <Badge color="ember" size="sm">Primary</Badge>}
                    {contact.isDecisionMaker && <Badge color="sky" size="sm">Decision maker</Badge>}
                    <span className="text-slate-500">{contact.title ?? contact.role.replace(/_/g, ' ')}</span>
                    <span className="ml-auto flex items-center gap-2 text-slate-600">
                      {contact.email && <span>{contact.email}</span>}
                      {contact.phone && <span>{formatPhone(contact.phone)}</span>}
                    </span>
                    {contact.verificationStatus === 'verified' ? (
                      <Badge color="green" size="sm">verified</Badge>
                    ) : contact.verificationStatus === 'invalid' ? (
                      <Badge color="red" size="sm">invalid</Badge>
                    ) : null}
                    {contact.consentBasis === 'none' && <Badge color="amber" size="sm">no consent</Badge>}
                  </button>
                ))
              )}
            </CardBody>
          </Card>

          {isPizzeria && (
            <Card>
              <CardHeader
                title="Kitchen equipment"
                description="What they cook on now — old ovens are the strongest signal."
                actions={
                  <Button size="sm" icon={Plus} onClick={() => setEquipmentOpen(true)}>
                    Add
                  </Button>
                }
              />
              <CardBody className="space-y-2">
                {lead.equipment.length === 0 ? (
                  <p className="text-[13px] text-slate-500">No equipment recorded. Ask what they bake on and how old it is.</p>
                ) : (
                  lead.equipment.map((item) => (
                    <div key={item.id} className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-[13px]">
                      <span className="font-medium text-slate-900">{EQUIPMENT_TYPE_LABELS[item.equipmentType as EquipmentType] ?? item.equipmentType}</span>
                      {item.brand && <span className="text-slate-600">{item.brand}</span>}
                      {item.model && <span className="text-slate-500">{item.model}</span>}
                      {item.ageYears !== null && (
                        <Badge color={item.ageYears >= 10 ? 'amber' : 'slate'} size="sm">
                          {item.ageYears} {pluralize(item.ageYears, 'year')} old
                        </Badge>
                      )}
                      {!item.isCurrent && <Badge color="gray" size="sm">previous</Badge>}
                      {item.condition !== 'unknown' && <span className="text-slate-500">{item.condition}</span>}
                    </div>
                  ))
                )}
              </CardBody>
            </Card>
          )}

          <InterestsAndTags lead={lead} />
          {lead.proposals.length > 0 && <ProposalsCard lead={lead} />}
          {lead.dataQuality.length > 0 && (
            <Card>
              <CardHeader title="Data quality" description="Small gaps that cost you replies." />
              <CardBody>
                <ul className="space-y-1.5 text-[13px]">
                  {lead.dataQuality.map((issue) => (
                    <li key={issue.code} className="flex items-start gap-2">
                      <AlertTriangle className={issue.severity === 'warning' ? 'mt-0.5 size-3.5 text-amber-600' : 'mt-0.5 size-3.5 text-slate-400'} />
                      <span className="text-slate-700">{issue.message}</span>
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          )}
          {lead.sourceRecords.length > 0 && (
            <Card>
              <CardHeader title="Where this record came from" description="Provenance for every source that touched it." />
              <CardBody>
                <ul className="space-y-1 text-[13px] text-slate-600">
                  {lead.sourceRecords.map((record) => (
                    <li key={record.id} className="flex flex-wrap items-center gap-2">
                      <Badge color="slate" size="sm">{record.provider}</Badge>
                      {record.sourceName && <span>{record.sourceName}</span>}
                      <span className="text-slate-400">fetched {formatDate(record.fetchedAt)}</span>
                      {record.sourceUrl && (
                        <a className="text-ember-700 hover:underline" href={record.sourceUrl} target="_blank" rel="noreferrer">
                          source
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          )}
        </div>

        <div className="space-y-4">
          {isPizzeria && <ScoreCard lead={lead} />}
          <LeadAutomationCard businessId={lead.id} />
          <LeadAiPanel businessId={lead.id} leadName={lead.name} />
          <Card>
            <CardHeader title="Relationship" />
            <CardBody className="space-y-3 text-[13px]">
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Assigned to</span>
                <OwnerAvatar userId={lead.ownerId} showName />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Stage</span>
                <StageBadge stageId={lead.stageId} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Last contact</span>
                <RelativeTime value={lead.counters.lastContactedAt} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Last response</span>
                <RelativeTime value={lead.counters.lastResponseAt} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Outreach attempts</span>
                <span className="text-slate-800">{lead.counters.outreachAttempts}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Next follow-up</span>
                <RelativeTime value={lead.counters.nextFollowUpAt} granularity="day" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Opportunity value</span>
                <MoneyText amount={lead.estimatedValue} currency={lead.currency} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Added</span>
                <span className="text-slate-700">{formatDate(lead.createdAt)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Last verified</span>
                <span className="text-slate-700">{lead.lastVerifiedAt ? formatDate(lead.lastVerifiedAt) : 'never'}</span>
              </div>
            </CardBody>
          </Card>
          <Card>
            <CardHeader title="Address" />
            <CardBody className="space-y-2 text-[13px] text-slate-700">
              <p>
                {[lead.addressLine1, lead.addressLine2].filter(Boolean).join(', ') || 'No street address'}
                <br />
                {[lead.city, lead.region, lead.postalCode].filter(Boolean).join(', ')}
                <br />
                {lead.country}
              </p>
              {lead.latitude !== null && lead.longitude !== null && (
                <iframe
                  title="Map"
                  className="h-40 w-full rounded border border-slate-200 dark:brightness-85"
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${lead.longitude - 0.01}%2C${lead.latitude - 0.008}%2C${lead.longitude + 0.01}%2C${lead.latitude + 0.008}&marker=${lead.latitude}%2C${lead.longitude}`}
                />
              )}
            </CardBody>
          </Card>
        </div>
      </div>

      {composer && (
        <EmailComposer businessId={id} businessName={lead.name} contactId={lead.contacts.find((c) => c.isPrimary)?.id ?? null} onClose={() => setComposer(false)} onSent={() => void invalidate()} />
      )}
      {logCall && <LogCallPanel businessId={id} onClose={() => setLogCall(false)} onSaved={() => void invalidate()} />}
      {logReply && <LogReplyPanel businessId={id} contacts={lead.contacts} onClose={() => setLogReply(false)} onSaved={() => void invalidate()} />}
      {noteOpen && <NotePanel businessId={id} onClose={() => setNoteOpen(false)} onSaved={() => void invalidate()} />}
      {(taskOpen || editingTask) && (
        <TaskFormPanel
          businessId={id}
          businessName={lead.name}
          task={editingTask ?? undefined}
          onClose={() => {
            setTaskOpen(false);
            setEditingTask(null);
          }}
        />
      )}
      {opportunityOpen && <NewOpportunityPanel businessId={id} onClose={() => setOpportunityOpen(false)} onCreated={() => void invalidate()} />}
      {contactOpen !== null && (
        <ContactPanel businessId={id} contact={contactOpen === 'new' ? null : contactOpen} onClose={() => setContactOpen(null)} onSaved={() => void invalidate()} />
      )}
      {equipmentOpen && <EquipmentPanel businessId={id} onClose={() => setEquipmentOpen(false)} onSaved={() => void invalidate()} />}
    </Page>
  );

  async function recordSignal(ruleKey: string) {
    try {
      await api.post(`/api/leads/${id}/signals`, { ruleKey });
      toast.success('Signal recorded — the score was recalculated');
      await invalidate();
    } catch (error) {
      toast.error(errorMessage(error));
    }
  }
}

/** Contacts have first/last name fields, not a single name. */
export function contactDisplayName(contact: Pick<ContactDTO, 'firstName' | 'lastName' | 'email'>): string {
  return [contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.email || 'Unnamed contact';
}
